import React from 'react';
import { Box, Button } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { styled } from '@mui/material/styles';

const MotionBox = motion.create(Box);

/** Film grain — light texture, pointer-events none. */
export const GrainOverlay: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      position: 'fixed',
      inset: 0,
      zIndex: 2,
      pointerEvents: 'none',
      opacity: 0.045,
      mixBlendMode: 'overlay',
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'repeat',
      backgroundSize: '180px 180px',
    }}
  />
);

/** Soft mesh blobs behind hero — CSS only, no WebGL. */
export const HeroMesh: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      position: 'absolute',
      inset: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 0,
      '@keyframes meshDrift': {
        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
        '50%': { transform: 'translate(2%, -3%) scale(1.05)' },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '& > *': { animation: 'none !important' },
      },
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: '-18%',
        right: '-8%',
        width: { xs: 280, md: 520 },
        height: { xs: 280, md: 520 },
        borderRadius: '50%',
        background:
          'radial-gradient(circle, rgba(30, 237, 159, 0.22) 0%, transparent 68%)',
        filter: 'blur(8px)',
        animation: 'meshDrift 14s ease-in-out infinite',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: '-22%',
        left: '-6%',
        width: { xs: 240, md: 420 },
        height: { xs: 240, md: 420 },
        borderRadius: '50%',
        background:
          'radial-gradient(circle, rgba(56, 189, 248, 0.12) 0%, transparent 70%)',
        filter: 'blur(10px)',
        animation: 'meshDrift 18s ease-in-out infinite reverse',
      }}
    />
  </Box>
);

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0 },
};

export const HeroStagger: React.FC<{
  children: React.ReactNode;
  sx?: object;
}> = ({ children, sx }) => {
  const reduce = useReducedMotion();
  if (reduce)
    return (
      <Box sx={{ width: '100%', ...((sx as object) || {}) }}>{children}</Box>
    );

  return (
    <MotionBox
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: 0.1, delayChildren: 0.08 } },
      }}
      initial="hidden"
      animate="show"
      sx={{ width: '100%', ...((sx as object) || {}) }}
    >
      {children}
    </MotionBox>
  );
};

export const HeroStaggerItem: React.FC<{
  children: React.ReactNode;
  sx?: object;
}> = ({ children, sx }) => {
  const reduce = useReducedMotion();
  if (reduce) return <Box sx={sx}>{children}</Box>;

  return (
    <MotionBox
      variants={fadeUp}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      sx={sx}
    >
      {children}
    </MotionBox>
  );
};

export const RevealOnScroll: React.FC<{
  children: React.ReactNode;
  sx?: object;
  delay?: number;
  id?: string;
}> = ({ children, sx, delay = 0, id }) => {
  const reduce = useReducedMotion();
  if (reduce)
    return (
      <Box id={id} sx={sx}>
        {children}
      </Box>
    );

  return (
    <MotionBox
      id={id}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18, margin: '0px 0px -6% 0px' }}
      transition={{
        duration: 0.6,
        delay,
        ease: [0.22, 1, 0.36, 1],
      }}
      sx={sx}
    >
      {children}
    </MotionBox>
  );
};

const MarqueeTrack = styled(Box)({
  display: 'flex',
  width: 'max-content',
  gap: '12px',
  '@keyframes homeMarquee': {
    from: { transform: 'translateX(0)' },
    to: { transform: 'translateX(-50%)' },
  },
  animation: 'homeMarquee 38s linear infinite',
  '&:hover': {
    animationPlayState: 'paused',
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',
  },
});

type MarqueeProps = {
  items: readonly string[];
  renderItem: (name: string, key: string) => React.ReactNode;
};

/** Infinite connector strip — duplicates items for seamless loop. */
export const ConnectorMarquee: React.FC<MarqueeProps> = ({
  items,
  renderItem,
}) => {
  const loop = [...items, ...items];
  return (
    <Box
      sx={{
        overflow: 'hidden',
        maskImage:
          'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
        WebkitMaskImage:
          'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
      }}
    >
      <MarqueeTrack>
        {loop.map((name, index) => renderItem(name, `${name}-${index}`))}
      </MarqueeTrack>
    </Box>
  );
};

type MotionCtaProps = React.ComponentProps<typeof Button> & {
  children: React.ReactNode;
};

/** Primary CTA with spring hover — respects reduced motion. */
export const MotionCta: React.FC<MotionCtaProps> = ({
  children,
  sx,
  ...rest
}) => {
  const reduce = useReducedMotion();

  const extraSx = (sx as Record<string, unknown> | undefined) ?? {};
  const { width: wrapperWidth, ...buttonExtra } = extraSx;

  return (
    <MotionBox
      component="span"
      whileHover={reduce ? undefined : { y: -2, scale: 1.02 }}
      whileTap={reduce ? undefined : { scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 420, damping: 28 }}
      sx={{
        display: 'inline-block',
        maxWidth: '100%',
        ...(wrapperWidth !== undefined ? { width: wrapperWidth as object } : {}),
      }}
    >
      <Button
        {...rest}
        sx={{
          backgroundColor: 'rgb(30, 237, 159)',
          color: '#000',
          fontWeight: 700,
          padding: '14px 30px',
          borderRadius: '999px',
          fontSize: '1rem',
          textTransform: 'none',
          boxShadow: '0 16px 40px rgba(30, 237, 159, 0.35)',
          width: wrapperWidth as object | undefined,
          '&:hover': {
            backgroundColor: 'rgb(25, 200, 130)',
            boxShadow: '0 18px 44px rgba(30, 237, 159, 0.42)',
          },
          ...buttonExtra,
        }}
      >
        {children}
      </Button>
    </MotionBox>
  );
};
